import { useEffect, useState, ChangeEvent } from 'react';
import Cookies from 'universal-cookie';
import InputField from './InputField';
import Card, { CollasableCard } from './Cards';
import { useTranslation } from 'react-i18next';

const cookies = new Cookies();

interface DilutionState {
  beerVolume: number;
  originalGravity: number;
  desiredGravity: number;
  waterSugarAddition: string;
  waterSugarAmount: string | number;
  waterSugarUnit: string;
  DMEAddition: boolean;
  DMEAmount?: string;
}

function calcDilution(beerVolume: number, originalGravity: number, desiredGravity: number): DilutionState {
  let waterSugarAddition = 'add_water';
  let waterSugarAmount: string | number;
  let waterSugarUnit = 'L';
  let DMEAddition = false;
  let DMEAmount: string | undefined;

  if (beerVolume > 0 && originalGravity > 0 && desiredGravity > 0) {
    if (desiredGravity < originalGravity) {
      waterSugarAmount = (((originalGravity - desiredGravity) / desiredGravity) * beerVolume).toFixed(2);
    } else {
      const addedSugar = (desiredGravity - originalGravity) * beerVolume * 11;
      waterSugarAddition = 'add_sugar';
      waterSugarAmount = addedSugar.toFixed(2);
      waterSugarUnit = 'g';
      DMEAddition = true;
      DMEAmount = (addedSugar / 0.8).toFixed(2);
    }
  } else {
    waterSugarAddition = '';
    waterSugarAmount = 0;
    waterSugarUnit = '';
  }

  return { beerVolume, originalGravity, desiredGravity, waterSugarAddition, waterSugarAmount, waterSugarUnit, DMEAddition, DMEAmount };
}

export default function Dilution() {
  const { t } = useTranslation();

  useEffect(() => {
    document.title = t('dilution.pageTitle');
  }, [t]);

  const [state, setState] = useState<DilutionState>(() =>
    calcDilution(
      cookies.get('dilution.beerVolume') || 20,
      cookies.get('dilution.originalGravity') || 16,
      cookies.get('dilution.desiredGravity') || 14,
    )
  );

  function handleOnChange(event: ChangeEvent<HTMLInputElement>) {
    const { name, value } = event.target;
    const v = Number(value);
    cookies.set(`dilution.${name}`, v);
    const { beerVolume, originalGravity, desiredGravity } = state;
    setState(calcDilution(
      name === 'beerVolume' ? v : beerVolume,
      name === 'originalGravity' ? v : originalGravity,
      name === 'desiredGravity' ? v : desiredGravity,
    ));
  }

  return (
    <div>
      <CollasableCard title={t('dilution.infoTitle')}>
        <p className="text-muted-foreground text-sm mb-2">
          {t('dilution.infoLine1')}
        </p>
        <p className="text-muted-foreground text-sm">
          {t('dilution.infoLine2')}
        </p>
      </CollasableCard>

      <div className="max-w-sm">
        <Card title={t('dilution.cardTitle')}>
          <InputField name="beerVolume" label={t('dilution.wortVolume')} step="0.1" value={state.beerVolume} unit="L" onChange={handleOnChange} />
          <InputField name="originalGravity" label={t('dilution.measuredGravity')} step="0.1" value={state.originalGravity} unit="BLG" onChange={handleOnChange} />
          <InputField name="desiredGravity" label={t('dilution.desiredGravity')} step="0.1" value={state.desiredGravity} unit="BLG" onChange={handleOnChange} />
          {state.waterSugarAddition && (
            <InputField
              label={state.waterSugarAddition === 'add_water' ? t('dilution.addWater') : t('dilution.addSugar')}
              value={state.waterSugarAmount}
              unit={state.waterSugarUnit}
              readOnly
            />
          )}
          {state.DMEAddition && (
            <InputField label={t('dilution.addLiquidExtract')} value={state.DMEAmount} unit="g" readOnly />
          )}
        </Card>
      </div>
    </div>
  );
}
